import Vue from 'vue';
import Router from 'vue-router';
import type { Route, RouteConfig } from 'vue-router';
import VueMeta from 'vue-meta';
import BookingPage from '@/components/pages/BookingPage.vue';
import FaqPage from '@/components/pages/FaqPage.vue';
import PrivacyPolicyPage from '@/components/pages/PrivacyPolicyPage.vue';
import TermsAndConditionsPage from '@/components/pages/TermsAndConditionsPage.vue';
import TripsPage from '@/components/pages/TripsPage.vue';
import TheMainContent from '@/components/TheMainContent.vue';
import SettingsPage from '@/components/pages/SettingsPage.vue';
import SettingsProfile from '@/components/settings/settingsProfile/SettingsProfile.vue';
import SettingsSecurity from '@/components/settings/settingsSecurity/SettingsSecurity.vue';
import NightCardPage from '@/components/pages/NightCardPage.vue';
import CommunePage from '@/components/nightCard/CommunePage.vue';
import { STrips } from '@slg/web-customer-shared';
import CheckoutPage from '@/components/pages/CheckoutPage.vue';
import ConfirmationPage from '@/components/pages/ConfirmationPage.vue';
import AuthPage from '@/components/pages/AuthPage.vue';
import ContactUsPage from '@/components/pages/ContactUsPage.vue';
import DeactivatedUserPage from '@/components/pages/DeactivatedUserPage.vue';
import SitemapPage from '@/components/pages/SitemapPage.vue';
import store from '@/store';
import appConfig from '@/appConfig';

Vue.use(VueMeta, {
  // optional pluginOptions
  refreshOnceOnNavigation: true,
});

Vue.use(Router);

function prefixRoutes(prefix: string, routes: RouteConfig[]): RouteConfig[] {
  return routes.map((route) => ({
    ...route,
    path: `${prefix}/${route.path}`,
  }));
}

const router = new Router({
  mode: 'history',
  scrollBehavior(to) {
    const el = document.getElementById('main-container');
    const timeOutMs = to.name === 'Checkout' ? 1500 : 0;
    setTimeout(() => {
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
      }
    }, timeOutMs);
  },
  routes: [
    {
      path: '/:lang',
      component: TheMainContent,
      redirect: (to) => ({ name: 'Booking', params: { lang: to.params.lang } }),
      children: [
        {
          path: 'booking',
          name: 'Booking',
          component: BookingPage,
        },
        {
          path: 'faq',
          name: 'FAQ',
          component: FaqPage,
        },
        {
          path: 'privacy-policy',
          name: 'PrivacyPolicy',
          component: PrivacyPolicyPage,
        },
        {
          path: 'terms-and-conditions',
          name: 'TermsAndConditions',
          component: TermsAndConditionsPage,
        },
        {
          path: 'checkout',
          name: 'Checkout',
          component: CheckoutPage,
        },
        {
          path: 'confirmation',
          name: 'Confirmation',
          component: ConfirmationPage,
        },
        {
          path: 'contact-us',
          name: 'ContactUs',
          component: ContactUsPage,
        },
        {
          path: 'communes',
          name: 'Communes',
          component: TheMainContent,
          redirect: { name: 'CommuneList' },
          children: [
            {
              path: '',
              name: 'CommuneList',
              component: NightCardPage,
            },
            {
              path: ':name',
              name: 'Commune',
              component: CommunePage,
            },
          ],
        },
        {
          path: 'auth',
          name: 'Auth',
          component: AuthPage,
        },
        {
          path: 'deactivated-user',
          name: 'DeactivatedUser',
          component: DeactivatedUserPage,
        },
        {
          path: 'sitemap',
          name: 'Sitemap',
          component: SitemapPage,
        },
        ...prefixRoutes('user',
          [
            {
              path: 'settings',
              redirect: { name: 'Profile' },
              component: SettingsPage,
              meta: { auth: true },
              children: [
                {
                  path: 'profile',
                  name: 'Profile',
                  meta: { auth: true },
                  component: SettingsProfile,
                },
                {
                  path: 'security',
                  name: 'Security',
                  meta: { auth: true },
                  component: SettingsSecurity,
                },
              ],
            },
            {
              path: 'trips',
              name: 'Trips',
              redirect: { name: 'Planned' },
              component: TripsPage,
              meta: { auth: true },
              children: [
                {
                  path: 'planned',
                  name: 'Planned',
                  meta: { auth: true },
                  component: STrips,
                },
                {
                  path: 'past',
                  name: 'Past',
                  meta: { auth: true },
                  component: STrips,
                },
                {
                  path: 'canceled',
                  name: 'Canceled',
                  meta: { auth: true },
                  component: STrips,
                },
              ],
            },
          ]),
      ],
    },
    {
      path: '*',
      redirect: { name: 'Booking', params: { lang: 'en' } },
    },
  ],
});

router.beforeEach(async (to: Route, from: Route, next) => {
  const authToke = localStorage.getItem('authToken');

  if (authToke) {
    try {
      await store.dispatch('auth/populateUserProfileInfo');
      store.commit('auth/setAuthToken', authToke);
    } catch (e) {
      store.commit('auth/clearAuthTokenAndProfileInfo');
    }
  }

  const requestedLang = (to.params.lang as string) || appConfig.defaultLanguage;
  const isUserLogged = store.getters['auth/getIsAuth'];

  if (to.meta.auth) {
    if (isUserLogged) {
      next();
    } else {
      next({ name: 'Booking', params: { lang: requestedLang } });
    }
    return;
  }

  if (store.getters['common/isGlobalLoading']) {
    store.commit('common/setGlobalLoading', false);
  }

  const temporaryReservationRemainingTime = store.getters['booking/getTemporaryReservationRemainingTime'];

  if (to.name !== 'Checkout' && to.name !== 'Confirmation') {
    if (isUserLogged && temporaryReservationRemainingTime > 0 && !to.params?.isErrorBookingConfirmation) {
      try {
        await store.dispatch('booking/cancelTemporaryReservation');
      } catch (e) {
        // todo
      }
    } else {
      store.commit('booking/clearTemporaryReservation');
    }
  }

  if (to.name === 'Checkout' && !store.getters['booking/getBookingRequestId']) {
    next({ name: 'Booking', params: { lang: requestedLang } });
    return;
  }

  if (to.name === 'Confirmation') {
    store.commit('booking/clearTemporaryReservation');
  }
  if (!appConfig.languages.includes(requestedLang)) {
    next({
      name: to.name,
      params: { ...to.params, lang: from.params.lang || appConfig.defaultLanguage },
      query: to.query,
    });
  } else {
    next();
  }
});

export default router;
